import { type MouseEvent, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { useMutation } from '@tanstack/react-query';
import BigNumber from 'bignumber.js';
import { DashboardModal } from '@/shared/components/dashboards/Modal';
import { Divider } from '@/shared/components/Divider';
import Button from '@/shared/components/flip-ui-kit/Button';
import TokenInputField from '@/shared/components/TokenInputField';
import { useStrategyLimits } from '@/shared/hooks/useStrategyLimits';
import useTracking from '@/shared/hooks/useTracking';
import CloseIcon from '@/shared/icons/flip-ui-kit/large/Close';
import WindowIcon from '@/shared/icons/flip-ui-kit/large/Window';
import { chainflipAssetMap } from '@/shared/utils/env';
import {
  type TradingStrategy,
  getStrategyTemplateUrl,
  maxWholeUnits,
} from '@/shared/utils/strategies';
import TokenAmount from '@/shared/utils/TokenAmount';
import { StrategyOverview } from './StrategyOverview';
import { StrategySuccessModal } from './StrategySuccessModal';
import { useExtrinsicSDK, useStateChainAccount } from '../../hooks/useStateChainAccount';
import { LiquidityProvisionEvents, type LiquidityProvisionTrackEvents } from '../../types/track';

export default function AddLiquidityToStrategyModal({
  strategy,
  open,
  onClose,
}: {
  strategy: TradingStrategy;
  open: boolean;
  onClose: () => void;
}) {
  const { freeBalances } = useStateChainAccount();

  const router = useRouter();
  const track = useTracking<LiquidityProvisionTrackEvents>();
  const { strategyAccountSs58Id: strategyIdSs58, baseAsset } = strategy;
  const quoteAsset = 'Usdc' as ChainflipAsset;

  const [baseAssetAmount, setBaseAssetAmount] = useState<TokenAmount>();
  const [quoteAssetAmount, setQuoteAssetAmount] = useState<TokenAmount>();
  const [baseInputError, setBaseInputError] = useState<string>();
  const [quoteInputError, setQuoteInputError] = useState<string>();
  const [dataBefore, setDataBefore] = useState(strategy);

  const resetData = () => {
    setBaseAssetAmount(undefined);
    setQuoteAssetAmount(undefined);
    setBaseInputError(undefined);
    setQuoteInputError(undefined);
  };

  useEffect(() => {
    setBaseInputError(undefined);
    setQuoteInputError(undefined);
  }, [quoteAssetAmount, baseAssetAmount]);

  const { data: limitsData } = useStrategyLimits();
  const baseMinimumDeposit =
    (baseAsset && limitsData?.[baseAsset]?.minimumAddedFundsAmount) ?? TokenAmount.ZERO;
  const quoteMinimumDeposit = limitsData?.[quoteAsset]?.minimumAddedFundsAmount ?? TokenAmount.ZERO;
  const minimumAddedFundsDeposit = maxWholeUnits(baseMinimumDeposit, quoteMinimumDeposit); // not sure if this is the protocol behavior

  const { sendExtrinsic, toast } = useExtrinsicSDK('addFundsToStrategy');

  const {
    mutate,
    isPending,
    isSuccess,
    reset: resetMutation,
  } = useMutation({
    mutationFn: async (_: MouseEvent<HTMLButtonElement>) => {
      track(LiquidityProvisionEvents.CtaAddAssLiquidity, {
        props: {
          path: window.location.pathname,
          position: 'ass_detail_page',
          scAccount: strategyIdSs58,
          key: strategy.templateKey,
          amount: baseAssetAmount?.toString() ?? '',
          asset: baseAsset ?? '',
          estApy: strategy.reportingData?.apyPercent.toString() ?? '',
        },
      });

      const totalAmount = BigNumber.sum(
        baseAssetAmount?.toBigNumber() ?? 0,
        quoteAssetAmount?.toBigNumber() ?? 0,
      );
      if (minimumAddedFundsDeposit.toBigNumber().gt(totalAmount)) {
        setBaseInputError(
          `Total asset amount must be greater than ${minimumAddedFundsDeposit.toFixed()}`,
        );
        setQuoteInputError(
          `Total asset amount must be greater than ${minimumAddedFundsDeposit.toFixed()}`,
        );
        return Promise.reject();
      }

      setDataBefore(strategy);

      return sendExtrinsic(
        strategyIdSs58,
        [
          [baseAsset, baseAssetAmount?.toString() ?? '0'],
          [quoteAsset, quoteAssetAmount?.toString() ?? '0'],
        ].filter(([, amount]) => amount !== '0') as [ChainflipAsset, string][],
      );
    },
    onSuccess() {
      track(LiquidityProvisionEvents.AddAssLiquiditySuccess, {
        props: {
          path: window.location.pathname,
          position: '{ass_copy_modal|ass_create_modal}',
          scAccount: strategyIdSs58,
          key: strategy.templateKey,
          amount: baseAssetAmount?.toString() ?? '',
          asset: baseAsset ?? '',
          estApy: strategy.reportingData?.apyPercent.toString() ?? '',
        },
      });
    },
    onError(err) {
      toast.error('Error while adding liquidity', { description: err.message });
    },
  });

  if (isSuccess) {
    return (
      <StrategySuccessModal
        open={open}
        onClose={onClose}
        afterLeave={() => {
          resetMutation();
          resetData();
        }}
        title="Liquidity Added"
        baseAsset={baseAsset}
      >
        <StrategyOverview
          strategy={dataBefore}
          apyPercent={dataBefore.reportingData?.apyPercent}
          newBaseAssetAmount={dataBefore.baseLiquidity.add(baseAssetAmount ?? 0)}
          newQuoteAssetAmount={dataBefore.quoteLiquidity.add(quoteAssetAmount ?? 0)}
        />
        <div className="flex flex-col gap-y-3">
          <Button
            className="w-full"
            type="primary-standard"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
              router.push(`/strategies/instance/${strategyIdSs58}`);
            }}
          >
            View Strategy Details
          </Button>
          <Button
            className="flex w-full items-center gap-x-1"
            type="secondary-standard"
            onClick={() => {
              window.open(
                new URL(getStrategyTemplateUrl(dataBefore), process.env.NEXT_PUBLIC_EXPLORER_URL),
              );
            }}
          >
            View on Explorer
            <WindowIcon />
          </Button>
        </div>
      </StrategySuccessModal>
    );
  }

  return (
    <DashboardModal
      open={open}
      onClose={onClose}
      afterLeave={() => {
        resetData();
        resetMutation();
      }}
    >
      <div className="flex w-[487px] flex-col gap-6 rounded-md border border-cf-gray-4 bg-cf-gray-3 p-6">
        <div className="flex w-full items-start justify-between">
          <div className="flex flex-col font-aeonikMedium">
            <span className="text-24 text-cf-white">Add Liquidity to {baseAsset} Strategy</span>
          </div>
          <button type="button" onClick={onClose} className="outline-cf-gray-5" tabIndex={-1}>
            <CloseIcon />
          </button>
        </div>
        <Divider className="h-px" />
        <div className="flex w-full flex-col gap-y-4 pb-2">
          <TokenInputField
            value={baseAssetAmount}
            onChange={setBaseAssetAmount}
            token={baseAsset && chainflipAssetMap[baseAsset]}
            balance={baseAsset && freeBalances?.[baseAsset]}
            error={baseInputError}
            tokenWidth={180}
            disabled={isPending}
          />
          <TokenInputField
            value={quoteAssetAmount}
            onChange={setQuoteAssetAmount}
            token={chainflipAssetMap.Usdc}
            balance={freeBalances?.Usdc}
            error={quoteInputError}
            tokenWidth={180}
            disabled={isPending}
          />
        </div>
        <Divider className="h-px" />
        <StrategyOverview
          strategy={strategy}
          apyPercent={strategy.reportingData?.apyPercent}
          newBaseAssetAmount={strategy.baseLiquidity.add(baseAssetAmount ?? 0)}
          newQuoteAssetAmount={strategy.quoteLiquidity.add(quoteAssetAmount ?? 0)}
        />
        <Divider className="h-px" />
        <div className="flex flex-row justify-end gap-x-4">
          <Button
            onClick={mutate}
            loading={isPending && 'iconOnly'}
            disabled={isPending}
            className="order-2"
          >
            Add liquidity
          </Button>
          <Button
            type="secondary-standard"
            onClick={onClose}
            disabled={isPending}
            className="order-1"
          >
            Cancel
          </Button>
        </div>
      </div>
    </DashboardModal>
  );
}
